/**
 * Speurtocht Notification Utilities
 * Helpers for sending notifications related to speurtocht events
 */

import { supabase } from "@/lib/supabase/client";
import { createInAppNotification } from "@/lib/notifications";
import { logActivity } from "@/lib/activity-log";
import { SpeurttochtNotificationConfig, DEFAULT_NOTIFICATION_CONFIG } from "../types";

/**
 * Template variable replacements
 */
interface TemplateVars {
  team_name?: string;
  user_name?: string;
  speurtocht_name?: string;
  step_number?: number;
  step_title?: string;
  duration?: string;
  points?: number;
  total_points?: number;
}

/**
 * Replace template variables in a string
 */
const replaceTemplateVars = (template: string, vars: TemplateVars): string => {
  let result = template;
  Object.entries(vars).forEach(([key, value]) => {
    result = result.replace(new RegExp(`{${key}}`, "g"), String(value ?? ""));
  });
  return result;
};

/**
 * Format duration in a human-readable format
 */
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours} uur en ${minutes} minuten`;
  }
  return `${minutes} minuten`;
};

/**
 * Get all users for a weekend
 */
const getWeekendUsers = async (weekendId: string): Promise<string[]> => {
  // Get all team members for this weekend
  const { data: teamMembers, error } = await supabase
    .from("team_members")
    .select("user_id, teams!inner(weekend_id)")
    .eq("teams.weekend_id", weekendId);

  if (error) {
    console.error("Error fetching weekend users:", error);
    return [];
  }

  // Get unique user IDs
  const userIds = [...new Set((teamMembers || []).map((tm) => tm.user_id))];
  return userIds;
};

/**
 * Get team members
 */
const getTeamMembers = async (teamId: string): Promise<string[]> => {
  const { data } = await supabase
    .from("team_members")
    .select("user_id")
    .eq("team_id", teamId);

  return (data || []).map((m) => m.user_id);
};

/**
 * Notify all users when a speurtocht is activated by admin
 */
export const notifySpeurttochtActivated = async ({
  speurttochtId,
  speurttochtName,
  speurttochtIcon,
  speurttochtColor,
  weekendId,
  activatedByUserId,
  activatedByUserName,
  stepsCount,
  maxDurationMinutes,
  config = DEFAULT_NOTIFICATION_CONFIG,
}: {
  speurttochtId: string;
  speurttochtName: string;
  speurttochtIcon: string;
  speurttochtColor: string;
  weekendId: string;
  activatedByUserId: string;
  activatedByUserName: string;
  stepsCount?: number;
  maxDurationMinutes?: number | null;
  config?: SpeurttochtNotificationConfig;
}): Promise<void> => {
  const onActivated = config.on_activated ?? DEFAULT_NOTIFICATION_CONFIG.on_activated;
  if (!onActivated.enabled) return;

  // Get all users for this weekend
  const recipients = await getWeekendUsers(weekendId);

  // Build details string for template
  let details = "";
  if (stepsCount) {
    details += `${stepsCount} stappen`;
  }
  if (maxDurationMinutes) {
    details += details ? ` • ${maxDurationMinutes} min` : `${maxDurationMinutes} min`;
  }

  const vars: TemplateVars = {
    speurtocht_name: speurttochtName,
    user_name: activatedByUserName,
  };

  let title = replaceTemplateVars(onActivated.title, vars);
  let body = replaceTemplateVars(onActivated.body, vars);

  // Append details if available
  if (details) {
    body += ` (${details})`;
  }

  // Create notifications for each recipient
  await Promise.all(
    recipients.map((userId) =>
      createInAppNotification({
        userId,
        title,
        body,
        type: "speurtocht_activated",
        actionUrl: `/(tabs)/games/speurtocht/${speurttochtId}`,
        actionData: {
          speurtocht_id: speurttochtId,
          speurtocht_name: speurttochtName,
        },
        weekendId,
        lottieAnimation: onActivated.lottie_animation || "party",
      })
    )
  );

  // Log activity
  await logActivity({
    user_id: activatedByUserId,
    action_type: "speurtocht.active",
    action_category: "game",
    target_type: "speurtocht",
    target_id: speurttochtId,
    target_name: speurttochtName,
    metadata: {
      activated_by: activatedByUserName,
      steps_count: stepsCount,
    },
    weekend_id: weekendId,
  });
};

/**
 * Notify when a team starts a speurtocht
 */
export const notifySpeurttochtStart = async ({
  speurttochtId,
  speurttochtName,
  teamId,
  teamName,
  weekendId,
  startedByUserId,
  startedByUserName,
  config = DEFAULT_NOTIFICATION_CONFIG,
}: {
  speurttochtId: string;
  speurttochtName: string;
  teamId: string;
  teamName: string;
  weekendId: string;
  startedByUserId: string;
  startedByUserName: string;
  config?: SpeurttochtNotificationConfig;
}): Promise<void> => {
  const onStart = config.on_start;
  if (!onStart.enabled) return;

  const vars: TemplateVars = {
    team_name: teamName,
    user_name: startedByUserName,
    speurtocht_name: speurttochtName,
  };

  const title = replaceTemplateVars(onStart.title, vars);
  const body = replaceTemplateVars(onStart.body, vars);

  // Get recipients
  let recipients: string[] = [];
  if (onStart.notify_all_users) {
    recipients = await getWeekendUsers(weekendId);
  } else {
    recipients = await getTeamMembers(teamId);
  }

  // Create notifications for each recipient
  await Promise.all(
    recipients.map((userId) =>
      createInAppNotification({
        userId,
        title,
        body,
        type: "speurtocht_start",
        actionUrl: `/(tabs)/games/speurtocht/${speurttochtId}`,
        actionData: {
          speurtocht_id: speurttochtId,
          team_id: teamId,
        },
        weekendId,
        lottieAnimation: "party",
      })
    )
  );

  // Log activity
  await logActivity({
    user_id: startedByUserId,
    action_type: "speurtocht.start",
    action_category: "game",
    target_type: "speurtocht",
    target_id: speurttochtId,
    target_name: speurttochtName,
    metadata: {
      team_id: teamId,
      team_name: teamName,
    },
    weekend_id: weekendId,
  });
};

/**
 * Notify when a step is completed
 */
export const notifyStepComplete = async ({
  speurttochtId,
  speurttochtName,
  sessionId,
  stepId,
  stepNumber,
  stepTitle,
  teamId,
  teamName,
  weekendId,
  completedByUserId,
  completedByUserName,
  pointsEarned,
  config = DEFAULT_NOTIFICATION_CONFIG,
}: {
  speurttochtId: string;
  speurttochtName: string;
  sessionId: string;
  stepId: string;
  stepNumber: number;
  stepTitle: string;
  teamId: string;
  teamName: string;
  weekendId: string;
  completedByUserId: string;
  completedByUserName: string;
  pointsEarned: number;
  config?: SpeurttochtNotificationConfig;
}): Promise<void> => {
  const onStepComplete = config.on_step_complete;
  if (!onStepComplete.enabled) return;

  const vars: TemplateVars = {
    team_name: teamName,
    user_name: completedByUserName,
    speurtocht_name: speurttochtName,
    step_number: stepNumber,
    step_title: stepTitle,
    points: pointsEarned,
  };

  const title = replaceTemplateVars(onStepComplete.title, vars);
  const body = replaceTemplateVars(onStepComplete.body, vars);

  // Get team members (excluding the one who completed)
  if (onStepComplete.notify_team) {
    const teamMembers = await getTeamMembers(teamId);
    const recipients = teamMembers.filter((id) => id !== completedByUserId);

    await Promise.all(
      recipients.map((userId) =>
        createInAppNotification({
          userId,
          title,
          body,
          type: "speurtocht_step_complete",
          actionUrl: `/(tabs)/games/speurtocht/${speurttochtId}`,
          actionData: {
            speurtocht_id: speurttochtId,
            session_id: sessionId,
            step_id: stepId,
          },
          weekendId,
        })
      )
    );
  }

  // Log activity
  await logActivity({
    user_id: completedByUserId,
    action_type: "speurtocht.step_complete",
    action_category: "game",
    target_type: "speurtocht_step",
    target_id: stepId,
    target_name: stepTitle,
    metadata: {
      speurtocht_id: speurttochtId,
      session_id: sessionId,
      step_number: stepNumber,
      team_id: teamId,
      points_earned: pointsEarned,
    },
    weekend_id: weekendId,
  });
};

/**
 * Notify when speurtocht is completed
 */
export const notifySpeurttochtComplete = async ({
  speurttochtId,
  speurttochtName,
  sessionId,
  teamId,
  teamName,
  weekendId,
  totalPoints,
  bonusPoints,
  durationSeconds,
  isFirstTeam,
  config = DEFAULT_NOTIFICATION_CONFIG,
}: {
  speurttochtId: string;
  speurttochtName: string;
  sessionId: string;
  teamId: string;
  teamName: string;
  weekendId: string;
  totalPoints: number;
  bonusPoints: number;
  durationSeconds: number;
  isFirstTeam: boolean;
  config?: SpeurttochtNotificationConfig;
}): Promise<void> => {
  const onComplete = config.on_complete;
  if (!onComplete.enabled) return;

  const vars: TemplateVars = {
    team_name: teamName,
    speurtocht_name: speurttochtName,
    duration: formatDuration(durationSeconds),
    total_points: totalPoints,
  };

  let title = replaceTemplateVars(onComplete.title, vars);
  let body = replaceTemplateVars(onComplete.body, vars);

  // Add first team bonus info
  if (isFirstTeam && bonusPoints > 0) {
    body += ` 🥇 Eerste team! +${bonusPoints} bonus punten!`;
  }

  // Get recipients
  let recipients: string[] = [];
  if (onComplete.notify_all_users) {
    recipients = await getWeekendUsers(weekendId);
  } else {
    recipients = await getTeamMembers(teamId);
  }

  // Create notifications
  await Promise.all(
    recipients.map((userId) =>
      createInAppNotification({
        userId,
        title,
        body,
        type: "speurtocht_complete",
        actionUrl: `/(tabs)/games/speurtocht/${speurttochtId}`,
        actionData: {
          speurtocht_id: speurttochtId,
          session_id: sessionId,
          team_id: teamId,
          is_first_team: isFirstTeam,
        },
        weekendId,
        lottieAnimation: isFirstTeam ? "trophy" : "confetti",
      })
    )
  );
};

/**
 * Notify when speurtocht expires (time ran out)
 */
export const notifySpeurttochtExpired = async ({
  speurttochtId,
  speurttochtName,
  sessionId,
  teamId,
  teamName,
  weekendId,
  completedSteps,
  totalSteps,
  pointsEarned,
  durationSeconds,
  config = DEFAULT_NOTIFICATION_CONFIG,
}: {
  speurttochtId: string;
  speurttochtName: string;
  sessionId: string;
  teamId: string;
  teamName: string;
  weekendId: string;
  completedSteps: number;
  totalSteps: number;
  pointsEarned: number;
  durationSeconds: number;
  config?: SpeurttochtNotificationConfig;
}): Promise<void> => {
  // Use on_expired config if available, otherwise use defaults
  const onExpired = config.on_expired ?? {
    enabled: true,
    notify_team: true,
    notify_all_users: true,
    title: "⏰ Tijd is op!",
    body: "{team_name} heeft {completed_steps}/{total_steps} stappen gehaald ({points} punten)",
    lottie_animation: "timer",
  };

  if (!onExpired.enabled) return;

  const vars: TemplateVars = {
    team_name: teamName,
    speurtocht_name: speurttochtName,
    duration: formatDuration(durationSeconds),
    total_points: pointsEarned,
  };

  // Custom template variables for expired
  let title = onExpired.title
    .replace("{team_name}", teamName)
    .replace("{speurtocht_name}", speurttochtName);
  
  let body = onExpired.body
    .replace("{team_name}", teamName)
    .replace("{speurtocht_name}", speurttochtName)
    .replace("{completed_steps}", String(completedSteps))
    .replace("{total_steps}", String(totalSteps))
    .replace("{points}", String(pointsEarned))
    .replace("{duration}", formatDuration(durationSeconds));

  // Get all users for this weekend (notify everyone)
  let allRecipients: string[] = [];
  if (onExpired.notify_all_users) {
    allRecipients = await getWeekendUsers(weekendId);
  }

  // Get team members for team-specific notification
  const teamMembers = await getTeamMembers(teamId);

  // Send different notifications to team vs others
  const teamTitle = "⏰ Tijd is op!";
  const teamBody = `Jullie hebben ${completedSteps}/${totalSteps} stappen gehaald en ${pointsEarned} punten verdiend voor het team!`;

  // Notify team members with their specific message
  if (onExpired.notify_team) {
    await Promise.all(
      teamMembers.map((userId) =>
        createInAppNotification({
          userId,
          title: teamTitle,
          body: teamBody,
          type: "speurtocht_expired",
          actionUrl: `/(tabs)/games/speurtocht/${speurttochtId}`,
          actionData: {
            speurtocht_id: speurttochtId,
            session_id: sessionId,
            team_id: teamId,
            points_earned: pointsEarned,
          },
          weekendId,
          lottieAnimation: onExpired.lottie_animation || "timer",
        })
      )
    );
  }

  // Notify all other users
  if (onExpired.notify_all_users) {
    const otherUsers = allRecipients.filter((id) => !teamMembers.includes(id));
    await Promise.all(
      otherUsers.map((userId) =>
        createInAppNotification({
          userId,
          title,
          body,
          type: "speurtocht_expired",
          actionUrl: `/(tabs)/games/speurtocht/${speurttochtId}`,
          actionData: {
            speurtocht_id: speurttochtId,
            session_id: sessionId,
            team_id: teamId,
          },
          weekendId,
          lottieAnimation: onExpired.lottie_animation || "timer",
        })
      )
    );
  }

  // Log activity
  await logActivity({
    user_id: teamMembers[0] || "system",
    action_type: "speurtocht.completed",
    action_category: "game",
    target_type: "speurtocht_session",
    target_id: sessionId,
    target_name: speurttochtName,
    metadata: {
      speurtocht_id: speurttochtId,
      team_id: teamId,
      team_name: teamName,
      completed_steps: completedSteps,
      total_steps: totalSteps,
      points_earned: pointsEarned,
      duration_seconds: durationSeconds,
      expired: true,
    },
    weekend_id: weekendId,
  });
};

/**
 * Award points to a team for speurtocht completion
 */
export const awardSpeurttochtPoints = async ({
  teamId,
  points,
  source,
  description,
  weekendId,
  awardedByUserId,
  speurttochtId,
  sessionId,
  stepId,
}: {
  teamId: string;
  points: number;
  source: "step" | "completion" | "first_team_bonus";
  description: string;
  weekendId: string;
  awardedByUserId: string;
  speurttochtId: string;
  sessionId: string;
  stepId?: string;
}): Promise<boolean> => {
  try {
    // Update team's total score
    const { error: updateError } = await supabase.rpc("increment_team_score", {
      p_team_id: teamId,
      p_points: points,
    });

    if (updateError) {
      // Fallback: direct update if RPC doesn't exist
      const { data: team } = await supabase
        .from("teams")
        .select("total_score")
        .eq("id", teamId)
        .single();

      if (team) {
        await supabase
          .from("teams")
          .update({ total_score: (team.total_score || 0) + points })
          .eq("id", teamId);
      }
    }

    // Log to score_history if table exists
    await supabase.from("score_history").insert({
      team_id: teamId,
      user_id: awardedByUserId,
      points,
      source: `speurtocht_${source}`,
      source_type: "speurtocht",
      source_id: stepId || sessionId,
      description,
      weekend_id: weekendId,
      metadata: {
        speurtocht_id: speurttochtId,
        session_id: sessionId,
        step_id: stepId,
      },
    });

    // Log activity
    await logActivity({
      user_id: awardedByUserId,
      action_type: "score.award",
      action_category: "score",
      target_type: "team",
      target_id: teamId,
      metadata: {
        points,
        source: `speurtocht_${source}`,
        speurtocht_id: speurttochtId,
        session_id: sessionId,
        step_id: stepId,
        description,
      },
      weekend_id: weekendId,
    });

    return true;
  } catch (err) {
    console.error("Error awarding speurtocht points:", err);
    return false;
  }
};

